import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Eye, Clock, User, Zap, Gauge, Thermometer, Target, Wifi, Settings, Activity, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { TiltViewFilters } from './ui/TiltViewFilters';
import { useTiltView } from '../redux/WeldData/hooks/useWeldData';
import { formatTimeForDisplay, formatDuration } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings, formatTimestampForDisplay } from '../utils/unitUtils';
import { getTiltStatus, getStatusColorClass } from '../utils/statusUtils';

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }
`;

// Define parameter limits (in inches - will be converted based on display unit)
const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 0.01, max: 10.0 },
  wireSpeed: { min: 150, max: 450 },
  totalWireConsumed: { min: 0, max: 1000 },
  distance: { min: 0, max: 1000 }
};

// Get converted parameter limits based on display unit
const getConvertedParameterLimits = () => {
  const displayUnit = getUnitFromSettings();
  
  if (displayUnit === 'Inches') {
    return PARAMETER_LIMITS;
  }
  
  // Convert limits from inches to mm for display
  return {
    voltage: PARAMETER_LIMITS.voltage,
    current: PARAMETER_LIMITS.current,
    travelSpeed: { 
      min: PARAMETER_LIMITS.travelSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.travelSpeed.max * 25.4 
    },
    heat: PARAMETER_LIMITS.heat,
    trueEnergy: PARAMETER_LIMITS.trueEnergy,
    oscillationWidth: { 
      min: PARAMETER_LIMITS.oscillationWidth.min * 25.4, 
      max: PARAMETER_LIMITS.oscillationWidth.max * 25.4 
    },
    wireSpeed: { 
      min: PARAMETER_LIMITS.wireSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.wireSpeed.max * 25.4 
    },
    totalWireConsumed: { 
      min: PARAMETER_LIMITS.totalWireConsumed.min * 25.4, 
      max: PARAMETER_LIMITS.totalWireConsumed.max * 25.4 
    },
    distance: { 
      min: PARAMETER_LIMITS.distance.min * 25.4, 
      max: PARAMETER_LIMITS.distance.max * 25.4 
    }
  };
};

const STATUS_CALCULATION_MIN_DELAY_MS = 20000;
const STATUS_CALCULATION_DELAY_PER_ROW_MS = 1000;

// Sorting types
interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

interface TiltViewTableProps {
  weld: any;
  onRowClick?: (row: TiltViewRow) => void;
  statusCalculationMethod?: string;
  filters?: {
    stationNameFilter: string;
    statusFilter: string;
    bugTypeFilter: string;
    torchFilter: string;
    passFilter: string;
    tiltRangeFilter: string;
    dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  };
  onFilterChange?: (filterType: string, value: any) => void;
  visibleColumns?: string[];
  statusCalculationStartedAt?: number | null;
}

// Define the type for a row
interface TiltViewRow {
  id: number;
  status: string;
  avg_staus_by_instantaneous?: string;
  stationName: string;
  bugType: string;
  torch: string;
  pass: string;
  tiltRange: string;
  weldStartTime: string;
  weldTime: number | null;
  autoincrement: string;
  welduid_s_event_torch?: string;
  bugNumber?: string;
  bandDiameter?: number | null;
  wireDiameter?: number | null;
  loggingDistance?: number | null;
  pipeDiameter?: number | null;
  current: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  volt: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  travelSpeed: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  trueEnergy: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  heat: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  wireSpeed: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  frequency: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  target: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  horizontalBias: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  totalWireConsumed: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  arcImpedance: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  oscillationWidth: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean };
  passView?: any;
  job_number?: string;
  jobNumber?: string;
}

// Helper function to check if a parameter is within limits
const isWithinLimits = (value: number, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
  const limits = getConvertedParameterLimits()[paramName];
  return value >= limits.min && value <= limits.max;
};

// Helper function to render min/avg/max values with proper formatting
// This will be redefined inside the component to access visibleColumns

const TiltViewTable: React.FC<TiltViewTableProps> = React.memo(({ weld, onRowClick, statusCalculationMethod = 'Instantaneous', filters, onFilterChange, visibleColumns, statusCalculationStartedAt = null }) => {
  const { tiltViewData, tiltViewLoading, tiltViewError, loadTiltView } = useTiltView();
  const [isStatusCalculationPending, setIsStatusCalculationPending] = useState(false);

  // Helper function to check if a column is visible
  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return true; // Show all columns if no visibility specified
    }
    return visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  // Helper function to check if a sub-column is visible
  const isSubColumnVisible = useCallback((columnKey: string, subType: 'min' | 'avg' | 'max'): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    const subKey = `${columnKey}-${subType}`;
    return visibleColumns.includes(subKey);
  }, [visibleColumns]);

  // Helper function to render min/avg/max values with proper formatting
  const renderMinAvgMax = useCallback((
    values?: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean }, 
    decimals: number = 2, 
    paramName?: keyof typeof PARAMETER_LIMITS,
    columnKey?: string
  ) => {
    if (!values || (values.min === null && values.avg === null && values.max === null)) {
      const visibleSubs: ('min' | 'avg' | 'max')[] = [];
      if (!columnKey || isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
      if (!columnKey || isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
      if (!columnKey || isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
      
      if (visibleSubs.length === 0) return null;
      
      return (
        <div className="grid gap-1 h-full" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
          {visibleSubs.map((_, idx) => (
            <div key={idx} className="text-gray-400 flex items-center justify-center h-full">-</div>
          ))}
        </div>
      );
    }

    const renderValue = (value: number | null, isAvg: boolean = false, status?: boolean) => {
      if (value === null) return '-';
      
      // Only convert distance-based parameters, not voltage, current, or impedance
      let displayValue;
      if (paramName && ['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth', 'totalWireConsumed', 'distance'].includes(paramName)) {
        // Convert value from inches to display unit for distance-based parameters
        const convertedValue = convertWeldDataValue(value, paramName);
        displayValue = typeof convertedValue === 'number' ? convertedValue.toFixed(decimals) : '-';
      } else {
        // Keep original value for voltage, current, impedance, etc.
        displayValue = value.toFixed(decimals);
      }
      
      // Apply red background if status is false, otherwise keep normal styling
      const bgColor = status === false ? 'bg-red-100' : 'bg-white';
      const textColor = status === false ? 'text-red-800' : (isAvg ? 'text-gray-900' : 'text-blue-600');
      
      return (
        <div className={`flex items-center justify-center h-full w-full ${bgColor} ${textColor} rounded-lg px-1 font-medium text-xs break-all`}>
          {displayValue}
        </div>
      );
    };

    // Determine which sub-columns are visible
    const visibleSubs: Array<{type: 'min' | 'avg' | 'max', value: number | null, status?: boolean}> = [];
    if (!columnKey || isSubColumnVisible(columnKey, 'min')) {
      visibleSubs.push({ type: 'min', value: values.min, status: values.lowStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'avg')) {
      visibleSubs.push({ type: 'avg', value: values.avg, status: values.avgStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'max')) {
      visibleSubs.push({ type: 'max', value: values.max, status: values.highStatus });
    }

    if (visibleSubs.length === 0) return null;

    return (
      <div className="grid gap-1 bg-gray-50 rounded-md h-full min-w-0" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map((sub) => (
          <div key={sub.type} className="flex items-center justify-center h-full min-w-0">
            <div className="flex items-center justify-center h-full w-full min-w-0">
              {renderValue(sub.value, sub.type === 'avg', sub.status)}
            </div>
          </div>
        ))}
      </div>
    );
  }, [isSubColumnVisible]);

  // Helper function to render header labels based on visible sub-columns
  const renderHeaderLabels = useCallback((columnKey: string) => {
    const visibleSubs: ('min' | 'avg' | 'max')[] = [];
    if (isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
    if (isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
    if (isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');
    
    if (visibleSubs.length === 0) return null;
    
    const labels: Record<'min' | 'avg' | 'max', string> = { min: 'Min', avg: 'Avg', max: 'Max' };
    
    return (
      <div className="grid text-xs font-normal text-center text-gray-500" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map(sub => (
          <div key={sub}>{labels[sub]}</div>
        ))}
      </div>
    );
  }, [isSubColumnVisible]);

  // Format job/wps numbers: if more than 2 values, show all comma-separated
  const formatJobNumbers = useCallback((value: string | string[] | null | undefined): string => {
    if (value == null || value === '') return '-';
    const parts = Array.isArray(value)
      ? value.filter((v) => v != null && String(v).trim() !== '')
      : String(value)
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean);
    if (parts.length === 0) return '-';
    return parts.join(', ');
  }, []);

  // Show job number column only when weld has multiple job numbers (comma/semicolon-separated)
  const jobNumberColumnVisible = useMemo(() => {
    const raw = weld?.wpsNumber;
    if (!raw || typeof raw !== 'string') return false;
    const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 1;
  }, [weld?.wpsNumber]);

  const isJobNumberColumnShown = isColumnVisible('job-number') && jobNumberColumnVisible;

  // Helper function to calculate sticky left position for a column
  const getStickyLeft = useCallback((columnKey: string): string => {
    const stickyColumnOrder = ['sl-no', 'status', 'job-number', 'station', 'actions'];
    const stickyColumnWidths: Record<string, number> = {
      'sl-no': 80,
      'status': 60,
      'job-number': 120,
      'station': 140,
      'actions': 100
    };
    const isStickyColVisible = (key: string) =>
      key === 'job-number' ? isJobNumberColumnShown : isColumnVisible(key);
    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      if (isStickyColVisible(stickyColumnOrder[i])) {
        left += stickyColumnWidths[stickyColumnOrder[i]];
      }
    }
    return `${left}px`;
  }, [isColumnVisible, isJobNumberColumnShown]);

  // Local filter states for TiltView specific filters
  const [stationNameFilter, setStationNameFilter] = useState('');
  const [bugTypeFilter, setBugTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [torchFilter, setTorchFilter] = useState<string[]>([]);
  const [passFilter, setPassFilter] = useState<string[]>([]);
  const [tiltRangeFilter, setTiltRangeFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });

  // Sorting state
  const [sorting, setSorting] = useState<Sorting | null>(null);

  // Check if we have any M500 data based on arc impedance values
  const hasM500Data = useMemo(() => {
    return tiltViewData.some((row: TiltViewRow) => {
      const hasArcImpedance = row.arcImpedance && (
        (row.arcImpedance.min !== null && row.arcImpedance.min !== undefined && row.arcImpedance.min !== 0) ||
        (row.arcImpedance.avg !== null && row.arcImpedance.avg !== undefined && row.arcImpedance.avg !== 0) ||
        (row.arcImpedance.max !== null && row.arcImpedance.max !== undefined && row.arcImpedance.max !== 0)
      );
      return hasArcImpedance;
    });
  }, [tiltViewData]);

// Get dynamic unit labels for table headers
const getUnitLabel = (parameter: string): string => {
  return getParameterUnitSuffix(parameter);
};

// Helper function to render converted min/max values for headers (only for distance-based parameters)
const renderHeaderLimits = (paramName: keyof typeof PARAMETER_LIMITS) => {
  const limits = getConvertedParameterLimits()[paramName];
  const minValue = limits.min.toFixed(0);
  const maxValue = limits.max.toFixed(0);
  
  return (
    <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
      <div>{minValue}</div>
      <div>Avg</div>
      <div>{maxValue}</div>
    </div>
  );
};

// Helper function to render static min/avg/max labels for non-distance parameters
const renderStaticHeaderLabels = () => {
  return (
    <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
      <div>Min</div>
      <div>Avg</div>
      <div>Max</div>
    </div>
  );
};



  // Sorting function
  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) {
      return <ChevronUp className="h-3 w-3 text-gray-400" />;
    }
    return sorting.order === 'ascend' ? 
      <ChevronUp className="h-3 w-3 text-blue-600" /> : 
      <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  // Extract unique options for dropdowns
  const uniqueOptions = useMemo(() => {
    const bugTypes = Array.from(new Set(tiltViewData.map((row: TiltViewRow) => row.bugType).filter((type: string | undefined): type is string => Boolean(type)))) as string[];
    const statuses = Array.from(new Set(tiltViewData.map((row: TiltViewRow) => row.status))) as string[];
    const torches = Array.from(new Set(tiltViewData.map((row: TiltViewRow) => row.torch).filter((torch: string | undefined): torch is string => Boolean(torch)))) as string[];
    const passes = Array.from(new Set(tiltViewData.map((row: TiltViewRow) => row.pass).filter((pass: string | undefined): pass is string => Boolean(pass)))) as string[];
    const tiltRanges = Array.from(new Set(tiltViewData.map((row: TiltViewRow) => row.tiltRange).filter((tiltRange: string | undefined): tiltRange is string => Boolean(tiltRange)))) as string[];
    
    return {
      bugTypes,
      statuses,
      torches,
      passes,
      tiltRanges
    };
  }, [tiltViewData]);

  // Apply filters to data
  const filteredData = useMemo(() => {
    let filtered = tiltViewData.filter((row: TiltViewRow) => {
      // Station name filter
      if (stationNameFilter && row.stationName) {
        const stationMatch = row.stationName.toLowerCase().includes(stationNameFilter.toLowerCase());
        if (!stationMatch) return false;
      }

      // Bug type filter
      if (bugTypeFilter.length > 0 && row.bugType && !bugTypeFilter.includes(row.bugType)) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = getTiltStatus(statusCalculationMethod, row);
        if (status && !statusFilter.includes(status)) {
          return false;
        }
      }

      // Torch filter
      if (torchFilter.length > 0 && row.torch && !torchFilter.includes(row.torch)) {
        return false;
      }

      // Pass filter
      if (passFilter.length > 0 && row.pass && !passFilter.includes(row.pass)) {
        return false;
      }

      // Tilt range filter
      if (tiltRangeFilter.length > 0 && row.tiltRange && !tiltRangeFilter.includes(row.tiltRange)) {
        return false;
      }

      // Date range filter
      if (dateRange.dateFrom || dateRange.dateTo) {
        const rowStartDate = row.weldStartTime ? new Date(row.weldStartTime) : null;
        
        if (rowStartDate) {
          const startDate = new Date(rowStartDate);
          startDate.setHours(0, 0, 0, 0);
          
          if (dateRange.dateFrom) {
            const fromDate = new Date(dateRange.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (startDate < fromDate) {
              return false;
            }
          }
          if (dateRange.dateTo) {
            const toDate = new Date(dateRange.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (startDate > toDate) {
              return false;
            }
          }
        }
      }

      return true;
    });

    // Apply sorting
    if (sorting) {
      filtered.sort((a: TiltViewRow, b: TiltViewRow) => {
        let aValue: any;
        let bValue: any;

        switch (sorting.field) {
          case 'stationName':
            aValue = a.stationName || '';
            bValue = b.stationName || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'bugType':
            aValue = a.bugType || '';
            bValue = b.bugType || '';
            break;
          case 'torch':
            aValue = a.torch || '';
            bValue = b.torch || '';
            break;
          case 'pass':
            aValue = a.pass || '';
            bValue = b.pass || '';
            break;
          case 'tiltRange':
            aValue = a.tiltRange || '';
            bValue = b.tiltRange || '';
            break;
          case 'weldTime':
            aValue = a.weldTime || 0;
            bValue = b.weldTime || 0;
            break;
          case 'weldStartTime':
            aValue = a.weldStartTime || '';
            bValue = b.weldStartTime || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sorting.order === 'ascend' ? -1 : 1;
        if (aValue > bValue) return sorting.order === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [tiltViewData, stationNameFilter, bugTypeFilter, statusFilter, torchFilter, passFilter, tiltRangeFilter, dateRange, sorting]);

  useEffect(() => {
    if (!statusCalculationStartedAt) {
      setIsStatusCalculationPending(false);
      return;
    }

    const rowCount = Math.max(tiltViewData.length, 1);
    const calculationDelay = Math.max(
      STATUS_CALCULATION_MIN_DELAY_MS,
      rowCount * STATUS_CALCULATION_DELAY_PER_ROW_MS,
    );
    const remainingDelay = calculationDelay - (Date.now() - statusCalculationStartedAt);

    if (remainingDelay <= 0) {
      setIsStatusCalculationPending(false);
      return;
    }

    setIsStatusCalculationPending(true);

    const timer = setTimeout(() => {
      if (weld?.weldNumber) {
        loadTiltView(weld.weldNumber);
      }
      setIsStatusCalculationPending(false);
    }, remainingDelay);

    return () => clearTimeout(timer);
  }, [statusCalculationStartedAt, tiltViewData.length, weld?.weldNumber, loadTiltView]);

  const renderTiltStatusIcon = (row: TiltViewRow) => {
    if (isStatusCalculationPending) {
      return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
    }

    const status = getTiltStatus(statusCalculationMethod, row);
    if (status === 'TRUE') {
      return <ThumbsUp className="h-5 w-5 text-green-600" />;
    }
    if (status === 'FALSE') {
      return <ThumbsDown className="h-5 w-5 text-red-600" />;
    }
    if (status === 'EX') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-sm bg-yellow-400 text-black rounded-full">!</span>;
    }
    if (status === 'CROSS') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-sm text-red-600">×</span>;
    }
    if (status === 'NULL' || status === null || status === undefined) {
      return <span className="text-gray-600">-</span>;
    }
    return <div className="h-5 w-5 rounded-full bg-yellow-400 animate-pulse" />;
  };

  return (
    <div className="h-full flex flex-col w-full" >
      <style>{scrollbarStyles}</style>

      {/* Filters Section */}
      <TiltViewFilters
        stationNameFilter={stationNameFilter}
        onStationNameFilterChange={setStationNameFilter}
        bugTypeFilter={bugTypeFilter}
        onBugTypeFilterChange={setBugTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        torchFilter={torchFilter}
        onTorchFilterChange={setTorchFilter}
        passFilter={passFilter}
        onPassFilterChange={setPassFilter}
        tiltRangeFilter={tiltRangeFilter}
        onTiltRangeFilterChange={setTiltRangeFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        bugTypeOptions={uniqueOptions.bugTypes}
        statusOptions={uniqueOptions.statuses}
        torchOptions={uniqueOptions.torches}
        passOptions={uniqueOptions.passes}
        tiltRangeOptions={uniqueOptions.tiltRanges}
        totalCount={tiltViewData.length}
        filteredCount={filteredData.length}
      />

      {/* Table Section */}
      <div className="flex-1 relative">
        {tiltViewLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading tilt view data...</span>
            </div>
          </div>
        ) : tiltViewError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-600 text-center">
              <p>Error loading tilt view data:</p>
              <p className="text-sm">{tiltViewError}</p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-center">
              <p>No tilt view data available for this weld.</p>
              {stationNameFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || torchFilter.length > 0 || passFilter.length > 0 || tiltRangeFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo ? (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div 
            className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorX: 'contain'
            }}
          >
            {/* Single unified table with sticky columns */}
            <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
              <thead className="sticky top-0 bg-white z-30">
                    <tr className="border-b h-[72px] bg-gray-50">
                  {/* Sticky columns */}
                  {isColumnVisible('sl-no') && (
                    <th className="sticky z-20 bg-gray-50 w-[80px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('sl-no') }}>
                      <div className="flex items-center justify-center gap-2">
                        <Target className="h-4 w-4" />
                        Sl.no
                      </div>
                    </th>
                  )}
                  {isColumnVisible('status') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[60px] w-[60px] max-w-[60px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ width: '60px', left: getStickyLeft('status') }}>
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Activity className="h-4 w-4" />
                        Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  )}
                  {isJobNumberColumnShown && (
                    <th className="sticky z-20 bg-gray-50 w-[120px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('job-number') }}>
                      Job Number
                    </th>
                  )}
                  {isColumnVisible('station') && (
                    <th className="sticky z-20 bg-gray-50 w-[140px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('station') }}>
                      <button
                        onClick={() => handleSort('stationName')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Station
                        {renderSortIcon('stationName')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('actions') && (
                    <th className="sticky z-20 bg-gray-50 w-[100px] px-4 text-center text-xs font-medium text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                      <div className="flex items-center justify-center gap-2">
                        <Settings className="h-4 w-4" />
                        Actions
                      </div>
                    </th>
                  )}
                  
                  {/* Scrollable columns */}
                  {isColumnVisible('bug-type') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('bugType')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Settings className="h-4 w-4" />
                        Bug Type
                        {renderSortIcon('bugType')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('torch') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('torch')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <User className="h-4 w-4" />
                        Torch
                        {renderSortIcon('torch')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('pass') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('pass')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Pass
                        {renderSortIcon('pass')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('tilt-range') && (
                    <th className="w-[140px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('tiltRange')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Tilt Range
                        {renderSortIcon('tiltRange')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-start-time') && (
                    <th className="w-[160px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('weldStartTime')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Clock className="h-4 w-4" />
                        Weld Start Time
                        {renderSortIcon('weldStartTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-time') && (
                    <th className="w-[80px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('weldTime')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Gauge className="h-4 w-4" />
                        Weld Time
                        {renderSortIcon('weldTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('auto-increment') && (
                    <th className="w-[160px] px-4 text-center text-xs font-medium text-gray-600 border-r">
                      <button
                        onClick={() => handleSort('autoincrement')}
                        className="flex items-center justify-center gap-2 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-4 w-4" />
                        Auto Increment
                        {renderSortIcon('autoincrement')}
                      </button>
                    </th>
                  )}
                  
                  {/* Parameter columns */}
                  {isColumnVisible('current') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Current ({getUnitLabel('current')})
                        </div>
                      </div>
                      {renderHeaderLabels('current')}
                    </th>
                  )}
                  {isColumnVisible('voltage') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Voltage ({getUnitLabel('volts')})
                        </div>
                      </div>
                      {renderHeaderLabels('voltage')}
                    </th>
                  )}
                  {isColumnVisible('travel-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Travel Speed ({getUnitLabel('travel_speed')})
                        </div>
                      </div>
                      {renderHeaderLabels('travel-speed')}
                    </th>
                  )}
                  {isColumnVisible('true-energy') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          True Energy ({getUnitLabel('true_energy')})
                        </div>
                      </div>
                      {renderHeaderLabels('true-energy')}
                    </th>
                  )}
                  {isColumnVisible('heat') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Thermometer className="h-4 w-4" />
                          Heat ({getUnitLabel('heat')})
                        </div>
                      </div>
                      {renderHeaderLabels('heat')}
                    </th>
                  )}
                  {isColumnVisible('wire-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Wire Speed ({getUnitLabel('wire_speed')})
                        </div>
                      </div>
                      {renderHeaderLabels('wire-speed')}
                    </th>
                  )}
                  {isColumnVisible('oscillation-width') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Wifi className="h-4 w-4" />
                          Oscillation Width ({getUnitLabel('oscillation_width')})
                        </div>
                      </div>
                      {renderHeaderLabels('oscillation-width')}
                    </th>
                  )}
                  {isColumnVisible('frequency') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          Frequency ({getUnitLabel('frequency')})
                        </div>
                      </div>
                      {renderHeaderLabels('frequency')}
                    </th>
                  )}
                  {isColumnVisible('target') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Target className="h-4 w-4" />
                          Target ({getUnitLabel('target')})
                        </div>
                      </div>
                      {renderHeaderLabels('target')}
                    </th>
                  )}
                  {isColumnVisible('horizontal-bias') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Horizontal Bias ({getUnitLabel('horizontal_bias')})
                        </div>
                      </div>
                      {renderHeaderLabels('horizontal-bias')}
                    </th>
                  )}
                  {isColumnVisible('total-wire-consumed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Total Wire Consumed ({getUnitLabel('total_wire_consumed')})
                        </div>
                      </div>
                      {renderHeaderLabels('total-wire-consumed')}
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('arc-impedance') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Arc Impedance ({getUnitLabel('arc_impedance')})
                        </div>
                      </div>
                      {renderHeaderLabels('arc-impedance')}
                    </th>
                  )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row: TiltViewRow, idx: number) => (
                  <tr
                    key={row.id}
                    className={`h-[52px] ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${idx < filteredData.length - 1 ? 'border-b' : ''}`}
                  >
                    {/* Sticky columns */}
                    {isColumnVisible('sl-no') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('sl-no') }}>{idx + 1}</td>
                    )}
                    {isColumnVisible('status') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} min-w-[60px] w-[60px] max-w-[60px] px-4 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ width: '60px', left: getStickyLeft('status') }}>
                        <div className="inline-flex justify-center">
                          {renderTiltStatusIcon(row)}
                        </div>
                      </td>
                    )}
                    {isJobNumberColumnShown && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('job-number') }}>{formatJobNumbers(row.jobNumber ?? row.job_number)}</td>
                    )}
                    {isColumnVisible('station') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('station') }}>{row.stationName || '-'}</td>
                    )}
                    {isColumnVisible('actions') && (
                      <td className={`sticky z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-4 text-center`} style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hover:bg-gray-100"
                          onClick={() => onRowClick?.(row)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                    
                    {/* Scrollable columns */}
                    {isColumnVisible('bug-type') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.bugType}</td>
                    )}
                    {isColumnVisible('torch') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.torch}</td>
                    )}
                    {isColumnVisible('pass') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.pass}</td>
                    )}
                    {isColumnVisible('tilt-range') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.tiltRange}</td>
                    )}
                    {isColumnVisible('weld-start-time') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{formatTimestampForDisplay(row.weldStartTime)}</td>
                    )}
                    {isColumnVisible('weld-time') && (
                      <td className="px-4 text-center text-gray-600 text-xs">{row.weldTime ? formatDuration(row.weldTime) : '-'}</td>
                    )}
                    {isColumnVisible('auto-increment') && (
                      <td className="px-4 text-center text-gray-600 text-xs border-r">{row.autoincrement || '-'}</td>
                    )}
                     
                     {/* Parameter columns */}
                     {isColumnVisible('current') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.current, 0, 'current', 'current')}</td>
                     )}
                     {isColumnVisible('voltage') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.volt, 2, 'voltage', 'voltage')}</td>
                     )}
                     {isColumnVisible('travel-speed') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.travelSpeed, 1, 'travelSpeed', 'travel-speed')}</td>
                     )}
                     {isColumnVisible('true-energy') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.trueEnergy, 2, 'trueEnergy', 'true-energy')}</td>
                     )}
                     {isColumnVisible('heat') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.heat, 2, 'heat', 'heat')}</td>
                     )}
                     {isColumnVisible('wire-speed') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.wireSpeed, 0, 'wireSpeed', 'wire-speed')}</td>
                     )}
                     {isColumnVisible('oscillation-width') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.oscillationWidth, 2, 'oscillationWidth', 'oscillation-width')}</td>
                     )}
                     {isColumnVisible('frequency') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.frequency, 0, undefined, 'frequency')}</td>
                     )}
                     {isColumnVisible('target') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.target, 0, undefined, 'target')}</td>
                     )}
                     {isColumnVisible('horizontal-bias') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.horizontalBias, 0, undefined, 'horizontal-bias')}</td>
                     )}
                     {isColumnVisible('total-wire-consumed') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.totalWireConsumed, 0, 'totalWireConsumed', 'total-wire-consumed')}</td>
                     )}
                     {hasM500Data && isColumnVisible('arc-impedance') && (
                       <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32">{renderMinAvgMax(row.arcImpedance, 0, undefined, 'arc-impedance')}</td>
                     )}
                      </tr>
                    ))}
                  </tbody>
                </table>
          </div>
        )}
      </div>
    </div>
  );
});

TiltViewTable.displayName = 'TiltViewTable';

export default TiltViewTable;